import { forwardRef, useCallback, useEffect } from "react";
import { cva } from "@stubhub/broadway-foundation/css";
import {
  Button,
  ButtonKind,
  ButtonSize,
  ButtonVariant,
  LegacyTagKind,
  LegacyTagSize,
  LegacyTagVariant,
  Tag,
} from "@stubhub/broadway-web";
import { AddFeature, RecordParticipationTypes, RemoveFeature, useSimpleFeatureTernary } from "@viagogo/feature";
import { Content, useContent } from "src/shared/components/content";
import { TruncateText } from "src/shared/components/TruncateText";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { isNaCobrands } from "src/shared/utils/appContextHelper";
import { ImageCarousel } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/image-carousel/ImageCarousel";
import { PerformerCardSocialLinks } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/PerformerCardSocialLinks";
import { ReigningChampion } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/reigning-champion/ReigningChampion";
import { TheaterRuntime } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/theater-runtime/TheaterRuntime";
import { UserContentCarousel } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/user-generated-content/UserContentCarousel";
import { EmbeddedYoutubePlayer } from "src/website-apps/responsive/stubhub-performer/components/performer-youtube-player";
import { SpotifyEmbeddedPlayer } from "src/website-apps/responsive/stubhub-performer/components/spotify-player";
import { SPOTIFY_PLAYER_SIZE } from "src/website-apps/responsive/stubhub-performer/components/spotify-player/constants";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { TOP_LEVEL_CATEGORY_IDS } from "src/website-apps/shared/components/explore-events/constants";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { usePerformerInfoCardData } from "src/website-apps/shared/hooks/usePerformerInfoCardData";
import { buttonWrapper, container, genreTagWrapper, spotifyPlayerHeight, titleDiv, titleDivSmall } from "./styles";

const mutedSectionRecipe = cva({
  base: {
    color: "text.subtle",
  },
  variants: {
    size: {
      default: {},
      small: {
        textStyle: "body.small",
      },
    },
  },
  defaultVariants: {
    size: "default",
  },
});

interface Props {
  /** handler when users clicks see events button at bottom of card */
  onClickSeeEvents?: () => void;
}

/**
 * Card to display information about a performer such as name, bio, social media links, etc.
 * TODO show on performer page below event list not on sep tab
 */
export const PerformerInfoCardMobileV2 = forwardRef<HTMLDivElement, Props>(({ onClickSeeEvents }, ref) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const model = usePerformerPageModelData();
  const {
    hasAnyData,
    hasNonModuleData,
    bio,
    genres,
    spotifyTopTrackId,
    youTubeVideoId,
    showDuration,
    catalogSourcedGenre,
    hasReigningChampionData,
    reigningChampions,
    categoryUserGeneratedImages,
  } = usePerformerInfoCardData();

  const maxLinesForBio = 4;
  const defaultImageAltText = useContent("cid$#4138909#"); // Performer

  useEffect(() => {
    if (hasAnyData) {
      storePageEvent(PE_TYPE.LOADED, PE_KEYS.MOBILE_PERFORMER_INFO_CARD_LOADED, "");
    }
  }, [hasAnyData, storePageEvent]);

  const handleClickSeeEvents = useCallback(() => {
    storePageEvent(
      PE_TYPE.CLICK,
      PE_KEYS.MOBILE_PERFORMER_CARD_SEE_EVENTS_CLICK,
      model.categorySummary.categoryName ?? "",
    );
    onClickSeeEvents?.();
  }, [onClickSeeEvents, model.categorySummary.categoryName, storePageEvent]);

  /**
   * fire when user scrolls to top of performer card
   * (since inViewTracker is absolute positioned cant fix it in middle)
   */
  const fireInViewTopPageEvent = useCallback(() => {
    storePageEvent(
      PE_TYPE.LOGGING,
      PE_KEYS.MOBILE_PERFORMER_CARD_SCROLLED_TO_TOP,
      model.categorySummary.categoryName ?? "",
    );
  }, [model.categorySummary.categoryName, storePageEvent]);

  /**
   * fire when user scrolls to bottom of performer card
   */
  const fireInViewBottomPageEvent = useCallback(() => {
    storePageEvent(
      PE_TYPE.LOGGING,
      PE_KEYS.MOBILE_PERFORMER_CARD_SCROLLED_TO_BOTTOM,
      model.categorySummary.categoryName ?? "",
    );
  }, [model.categorySummary.categoryName, storePageEvent]);

  const resolvedTitleDivClasses =
    useSimpleFeatureTernary(FeatureSetEnum.TMPerformerInfoCardSmallerText, titleDiv, titleDivSmall, () => hasAnyData) ??
    titleDiv;
  const resolvedBioStyleClasses =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerInfoCardSmallerText,
      mutedSectionRecipe(),
      mutedSectionRecipe({ size: "small" }),
      () => hasAnyData,
    ) ?? mutedSectionRecipe();

  const hasGenreBadges = !!catalogSourcedGenre || (!!genres && genres.length > 0);
  const hasSpotifyYoutubeGenreModule = !!(spotifyTopTrackId || youTubeVideoId || catalogSourcedGenre || genres?.length);
  // AB 104179 — this card also renders on tablet; ClientApplicationId 3 targeting scopes it to non-mobile clients.
  // Participation is recorded server-side at page load; this gate only controls rendering.
  const isSpotifyYoutubeGenreRemoved =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerRemoveSpotifyYoutubeGenreTags,
      false,
      true,
      () => hasSpotifyYoutubeGenreModule,
      false,
    ) ?? false;

  // Negative test (CWP-2943): mobile-only variant 1 hides the Spotify player + genre badge module.
  // Auto-record is off; participation is recorded from the in-view sentinel so users who never
  // scroll to the module are not enrolled.
  const hasSpotifyGenreBadgeModule = !!spotifyTopTrackId || hasGenreBadges;
  const hideSpotifyGenreBadge =
    useSimpleFeatureTernary(
      FeatureSetEnum.MPerformerRemoveSpotifyGenreBadge,
      false,
      true,
      () => hasSpotifyGenreBadgeModule,
      false,
    ) ?? false;

  // Either experiment can remove the Spotify player + genre badges on its own surface.
  const isGenreBadgeOrSpotifyRemoved = isSpotifyYoutubeGenreRemoved || hideSpotifyGenreBadge;

  const hasNonRemovedData = isSpotifyYoutubeGenreRemoved ? hasNonModuleData : hasAnyData;
  const hasCarouselImages = (model.categoryMetadata?.common?.infoCardCarouselImageUrls?.length ?? 0) > 0;

  // Removal telemetry fires once the card is seen (telemetry only; participation is recorded server-side).
  const fireModuleRemovalEvents = useCallback(() => {
    if (!isSpotifyYoutubeGenreRemoved) return;
    const categoryId = String(model.categorySummary.categoryId ?? "");
    if (spotifyTopTrackId) {
      storePageEvent(PE_TYPE.FEATURETRACKING, PE_KEYS.PERFORMER_INFO_CARD_SPOTIFY_REMOVED, categoryId);
    }
    if (youTubeVideoId) {
      storePageEvent(PE_TYPE.FEATURETRACKING, PE_KEYS.PERFORMER_INFO_CARD_YOUTUBE_REMOVED, categoryId);
    }
  }, [
    isSpotifyYoutubeGenreRemoved,
    spotifyTopTrackId,
    youTubeVideoId,
    storePageEvent,
    model.categorySummary.categoryId,
  ]);

  // Removal telemetry and the mobile-only experiment sentinel must stay mounted even when the
  // variant hides every visible child.
  const moduleRemovalSentinel = (
    <>
      <AddFeature
        id={FeatureSetEnum.MPerformerRemoveSpotifyGenreBadge}
        canParticipate={() => hasSpotifyGenreBadgeModule}
        recordType={RecordParticipationTypes.IN_VIEW}
        feature={() => null}
      />
      <InViewTracker onInView={fireModuleRemovalEvents} />
    </>
  );

  if (!hasNonRemovedData && !hasCarouselImages) {
    return hasSpotifyYoutubeGenreModule ? <div ref={ref}>{moduleRemovalSentinel}</div> : null;
  }

  const performerName = model.categorySummary.categoryName;
  const isSportsCategory = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.SPORT;
  return (
    <div className={container} ref={ref}>
      {moduleRemovalSentinel}
      <div className={resolvedTitleDivClasses}>
        {/* "About" */}
        <Content id="cid$#3715760#" />
        {performerName && ` ${performerName}`}
        {!isGenreBadgeOrSpotifyRemoved && hasGenreBadges && (
          <div className={genreTagWrapper}>
            {(catalogSourcedGenre ? [catalogSourcedGenre] : (genres ?? [])).map((genre) => (
              <Tag
                key={genre}
                kind={LegacyTagKind.Brand}
                size={LegacyTagSize.Small}
                variant={LegacyTagVariant.Borderless}
              >
                {genre}
              </Tag>
            ))}
          </div>
        )}
      </div>
      {!!showDuration && (
        <RemoveFeature
          id={FeatureSetEnum.TMPerformerRemovePlaybillShowDuration}
          canParticipate={() => !!showDuration}
          recordType={RecordParticipationTypes.IN_VIEW}
          feature={() => <TheaterRuntime durationString={showDuration!} />}
        />
      )}
      {bio?.length ? (
        <RemoveFeature
          id={FeatureSetEnum.TMPerformerRemoveBioDiagnostic}
          canParticipate={() => isNaCobrands(appContext)}
          recordType={RecordParticipationTypes.IN_VIEW}
          feature={() => (
            <RemoveFeature
              id={FeatureSetEnum.TMINTLRemoveBio}
              canParticipate={() => !isNaCobrands(appContext)}
              recordType={RecordParticipationTypes.IN_VIEW}
              feature={() => (
                <div className={resolvedBioStyleClasses}>
                  <TruncateText
                    isModal
                    modalTitle={performerName ?? ""}
                    maxLines={maxLinesForBio}
                    pageEventArgs={[PE_TYPE.CLICK, PE_KEYS.PERFORMER_INFO_CARD_BIO_SEE_MORE, performerName ?? ""]}
                    text={bio}
                    seeMoreOnNewLine
                  />
                </div>
              )}
            />
          )}
        />
      ) : null}
      <InViewTracker onInView={fireInViewTopPageEvent} />
      {!!hasReigningChampionData && <ReigningChampion reigningChampionData={reigningChampions} />}

      {!isSpotifyYoutubeGenreRemoved && !!youTubeVideoId && <EmbeddedYoutubePlayer videoId={youTubeVideoId} />}
      {!isGenreBadgeOrSpotifyRemoved && !!spotifyTopTrackId && (
        <SpotifyEmbeddedPlayer
          className={spotifyPlayerHeight}
          height={SPOTIFY_PLAYER_SIZE.SMALL}
          trackId={spotifyTopTrackId}
        />
      )}
      {!!categoryUserGeneratedImages && (
        <UserContentCarousel
          images={categoryUserGeneratedImages}
          categoryName={performerName ?? ""}
          isSportsFanImages={isSportsCategory}
        />
      )}

      {(model.categoryMetadata?.common?.infoCardCarouselImageUrls?.length ?? 0) > 0 && (
        <ImageCarousel
          images={model.categoryMetadata?.common?.infoCardCarouselImageUrls ?? []}
          imageLabel={performerName ?? model.categorySummary.categoryName ?? defaultImageAltText}
          autoRotate
        />
      )}

      <PerformerCardSocialLinks />
      <div className={buttonWrapper}>
        <InViewTracker onInView={fireInViewBottomPageEvent} />
        <Button
          kind={ButtonKind.Neutral}
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Small}
          onClick={handleClickSeeEvents}
        >
          {/* See events */}
          <Content id="cid$#3715759#" />
        </Button>
      </div>
    </div>
  );
});
PerformerInfoCardMobileV2.displayName = "PerformerInfoCardMobileV2";
